import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { normalizeShippingSettings, DEFAULT_QUIKBAK_FEE, normalizeCountryCode } from '../utils/shippingUtils';
import CountryDropdown from '../components/CountryDropdown';
import { getCountryFlag, getCountryNameFromCode } from '../utils/countryUtils';

const AUTHORIZED_EMAIL = 'daniel@quikbak.com';

const sizeKeys = ['small', 'medium', 'large'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
};

export default function ShippingSettingsPage() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(() => normalizeShippingSettings());
  const [newCountry, setNewCountry] = useState('');

  const authorized = currentUser?.email === AUTHORIZED_EMAIL;

  useEffect(() => {
    let isMounted = true;

    async function fetchSettings() {
      if (!authorized) {
        setLoading(false);
        return;
      }

      try {
        const settingsRef = doc(db, 'settings', 'shipping');
        const snapshot = await getDoc(settingsRef);
        if (!isMounted) return;
        const normalized = normalizeShippingSettings(snapshot.exists() ? snapshot.data() : {});
        setFormState(normalized);
      } catch (fetchError) {
        console.error('Failed to load shipping settings:', fetchError);
        if (isMounted) {
          setError(t('shippingSettings.error'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchSettings();

    return () => {
      isMounted = false;
    };
  }, [authorized, t]);

  const countryEntries = useMemo(() => {
    return Object.entries(formState.countries || {}).sort(([a], [b]) => {
      const nameA = getCountryNameFromCode(a) || a;
      const nameB = getCountryNameFromCode(b) || b;
      return nameA.localeCompare(nameB);
    });
  }, [formState.countries]);

  const handleDefaultRateChange = (size, value) => {
    setFormState((prev) => ({
      ...prev,
      defaultRates: {
        ...prev.defaultRates,
        [size]: value,
      },
    }));
  };

  const handleQuikbakFeeChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      quikbakFee: value,
    }));
  };

  const handleCountryRateChange = (country, size, value) => {
    setFormState((prev) => ({
      ...prev,
      countries: {
        ...prev.countries,
        [country]: {
          ...prev.countries[country],
          [size]: value,
        },
      },
    }));
  };

  const handleAddCountry = () => {
    const normalizedCountry = normalizeCountryCode(newCountry);
    if (!normalizedCountry) {
      return;
    }
    if (formState.countries?.[normalizedCountry]) {
      setNewCountry('');
      return;
    }
    setFormState((prev) => ({
      ...prev,
      countries: {
        ...prev.countries,
        [normalizedCountry]: {
          small: prev.defaultRates.small ?? 0,
          medium: prev.defaultRates.medium ?? 0,
          large: prev.defaultRates.large ?? 0,
        },
      },
    }));
    setNewCountry('');
  };

  const handleRemoveCountry = (country) => {
    setFormState((prev) => {
      const updated = { ...prev.countries };
      delete updated[country];
      return { ...prev, countries: updated };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const sanitizedDefaultRates = sizeKeys.reduce((acc, key) => {
        acc[key] = toNumber(formState.defaultRates?.[key]);
        return acc;
      }, {});

      const sanitizedCountries = Object.entries(formState.countries || {}).reduce((acc, [country, rates]) => {
        const normalizedCountry = normalizeCountryCode(country);
        if (!normalizedCountry) {
          return acc;
        }
        const sanitizedRates = sizeKeys.reduce((rateAcc, key) => {
          rateAcc[key] = toNumber(rates?.[key]);
          return rateAcc;
        }, {});
        acc[normalizedCountry] = sanitizedRates;
        return acc;
      }, {});

      const payload = {
        defaultRates: sanitizedDefaultRates,
        countries: sanitizedCountries,
        quikbakFee: toNumber(formState.quikbakFee),
      };

      await setDoc(doc(db, 'settings', 'shipping'), payload, { merge: false });
      setSuccess(t('shippingSettings.success'));
    } catch (saveError) {
      console.error('Failed to save shipping settings:', saveError);
      setError(t('shippingSettings.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="shipping-settings-page">
        <h2>{t('shippingSettings.title')}</h2>
        <div className="info-message">{t('permissions.validation.needLogin')}</div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="shipping-settings-page">
        <h2>{t('shippingSettings.title')}</h2>
        <div className="error-message">{t('shippingSettings.noAccess')}</div>
      </div>
    );
  }

  return (
    <div className="shipping-settings-page">
      <h2>{t('shippingSettings.title')}</h2>
      <p className="shipping-settings-description">{t('shippingSettings.description')}</p>

      {loading ? (
        <div className="loading-container">{t('shippingSettings.loading')}</div>
      ) : (
        <form className="shipping-settings-form" onSubmit={handleSave}>
          <section className="shipping-settings-section">
            <h3>{t('shippingSettings.defaultRates')}</h3>
            <div className="shipping-settings-grid">
              {sizeKeys.map((size) => (
                <label key={size} className="shipping-settings-field">
                  <span>{t(`shippingSettings.${size}`)}</span>
                  <input
                    type="number"
                    min="0"
                    value={formState.defaultRates?.[size] ?? ''}
                    onChange={(event) => handleDefaultRateChange(size, event.target.value)}
                    required
                  />
                </label>
              ))}
              <label className="shipping-settings-field">
                <span>{t('shippingSettings.quikbakFee')}</span>
                <input
                  type="number"
                  min="0"
                  value={formState.quikbakFee ?? DEFAULT_QUIKBAK_FEE}
                  onChange={(event) => handleQuikbakFeeChange(event.target.value)}
                  required
                />
              </label>
            </div>
          </section>

          <section className="shipping-settings-section">
            <h3>{t('shippingSettings.countryHeader')}</h3>

            <div className="add-country-row">
              <CountryDropdown
                value={newCountry}
                onChange={setNewCountry}
                placeholder={t('shippingSettings.addCountryPlaceholder')}
                valueType="isoCode"
              />
              <button type="button" className="secondary-button" onClick={handleAddCountry}>
                {t('shippingSettings.addCountry')}
              </button>
            </div>

            {countryEntries.length === 0 ? (
              <div className="info-message">{t('adminDashboard.table.noData')}</div>
            ) : (
              <div className="shipping-settings-table">
                <div className="shipping-settings-table-header">
                  <span>{t('shippingSettings.countryHeader')}</span>
                  {sizeKeys.map((size) => (
                    <span key={size}>{t(`shippingSettings.${size}`)}</span>
                  ))}
                  <span>{t('shippingSettings.remove')}</span>
                </div>
                {countryEntries.map(([country, rates]) => (
                  <div key={country} className="shipping-settings-table-row">
                    <span>
                      <span style={{ marginRight: '6px' }}>{getCountryFlag(country)}</span>
                      {`${getCountryNameFromCode(country)} (${country})`}
                    </span>
                    {sizeKeys.map((size) => (
                      <input
                        key={size}
                        type="number"
                        min="0"
                        value={rates?.[size] ?? ''}
                        onChange={(event) => handleCountryRateChange(country, size, event.target.value)}
                        required
                      />
                    ))}
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => handleRemoveCountry(country)}
                    >
                      {t('shippingSettings.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && <div className="error-message" role="alert">{error}</div>}
          {success && <div className="success-message" role="status">{success}</div>}

          <div className="shipping-settings-actions">
            <button type="submit" className="payment-submit-button" disabled={saving}>
              {saving ? t('shippingSettings.saving') : t('shippingSettings.save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
